type TourReportCheckpointRow = {
  index: number;
  checkpointName: string;
  status: "Scanned" | "Missed";
  scanTimeLabel: string;
  isDoubleHit?: boolean;
  isQuickPatrol?: boolean;
  hasRuleViolation?: boolean;
};

export type TourReportPdfInput = {
  logoSrc?: string;
  hqAddressLines: string[];
  reportDateLabel: string;
  reportTimeLabel: string;
  tourName: string;
  guardName: string;
  tourSessionId: number;
  averageSitePatrolTimeLabel: string;
  averageTravelTimeLabel: string;
  realTourDurationLabel: string;
  siteDoubleHitsLabel: string;
  siteQuickPatrolsLabel: string;
  checkpointCompletionLabel: string;
  checkpoints: TourReportCheckpointRow[];
};

type PdfImage = { width: number; height: number; bytes: Uint8Array };

type RowBadge = { label: string; bg: string; border: string; text: string };

type PreparedRow = {
  index: number;
  status: "Scanned" | "Missed";
  scanTimeLabel: string;
  nameLines: string[];
  badges: RowBadge[];
  rowHeight: number;
};

// ── Layout ──────────────────────────────────────────────────────────────────
const SCALE = 3;
const u = (pt: number) => Math.round(pt * SCALE);

const PDF_W = 612;
const PDF_H = 792;
const PW = u(PDF_W);
const PH = u(PDF_H);
const MARGIN = u(44);
const CW = PW - MARGIN * 2;
const FOOTER_AT = u(758);
const CONTENT_BOTTOM = u(750);

// ── Dark palette (matches the app) ──────────────────────────────────────────
const C = {
  pageBg: "#0f0f0f",
  cardBg: "#161616",
  cardBorder: "#262626",
  accent: "#8B0000",
  accentDim: "#3a1212",
  white: "#ffffff",
  text: "rgba(255,255,255,0.88)",
  textSec: "rgba(255,255,255,0.58)",
  textMuted: "rgba(255,255,255,0.34)",
  textDim: "rgba(255,255,255,0.18)",
  divider: "#1e1e1e",
  pillScannedBg: "rgba(34,197,94,0.12)",
  pillScannedBorder: "rgba(34,197,94,0.40)",
  pillScannedText: "#6fe3a4",
  pillMissedBg: "rgba(239,68,68,0.12)",
  pillMissedBorder: "rgba(239,68,68,0.40)",
  pillMissedText: "#ff8f8f",
  headerRowBg: "#121212",
  doubleHitBg: "rgba(139,0,0,0.26)",
  doubleHitBorder: "rgba(255,143,143,0.35)",
  doubleHitText: "#ff8f8f",
  quickPatrolBg: "rgba(16,77,109,0.30)",
  quickPatrolBorder: "rgba(132,217,255,0.36)",
  quickPatrolText: "#84d9ff",
  violationBg: "rgba(139,0,0,0.26)",
  violationBorder: "rgba(255,143,143,0.35)",
  violationText: "#ef7878",
} as const;

// ── Fonts (Fira Code primary — matches the app) ────────────────────────────
const MONO = "'Fira Code', 'SF Mono', 'Courier New', monospace";
const SANS = "'Manrope', 'Inter', 'Helvetica Neue', Arial, sans-serif";

const mk = (w: number, pt: number, f: string) => `${w} ${u(pt)}px ${f}`;

const F = {
  title:      mk(900, 13, MONO),     LH_TITLE:      u(17),
  titleSub:   mk(400, 8.5, MONO),
  label:      mk(800, 7, MONO),      LH_LABEL:      u(9),
  value:      mk(500, 10, SANS),     LH_VALUE:      u(13),
  metricVal:  mk(900, 11, SANS),     LH_MVAL:       u(14),
  metricLbl:  mk(800, 6.5, MONO),    LH_MLBL:       u(9),
  sectionLbl: mk(800, 7.5, MONO),    LH_SECTION:    u(10),
  summaryTxt: mk(600, 7.5, MONO),    LH_SUMMARY:    u(10),
  th:         mk(700, 7, MONO),
  td:         mk(400, 9, SANS),      LH_TD:         u(12),
  tdMono:     mk(500, 8, MONO),
  pill:       mk(600, 6, MONO),
  footer:     mk(400, 6, MONO),
};

// ── Text / encoding helpers ─────────────────────────────────────────────────

function safe(v: unknown): string {
  return String(v ?? "").normalize("NFKD").replace(/[^\x20-\x7E]/g, " ").replace(/\s+/g, " ").trim();
}
function enc(v: string) { return new TextEncoder().encode(v); }
function b64(s: string) {
  const b = atob(s); const a = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) a[i] = b.charCodeAt(i); return a;
}
function cat(p: Uint8Array[]) {
  const t = p.reduce((s, x) => s + x.length, 0); const o = new Uint8Array(t);
  let off = 0; for (const x of p) { o.set(x, off); off += x.length; } return o;
}

// ── PDF builder ─────────────────────────────────────────────────────────────

class PdfBuilder {
  private o: Array<string | Uint8Array | null> = [];
  res() { this.o.push(null); return this.o.length; }
  add(c: string | Uint8Array) { const id = this.res(); this.put(id, c); return id; }
  put(id: number, c: string | Uint8Array) { this.o[id - 1] = c; }
  build(root: number) {
    const h = enc("%PDF-1.4\n%\xFF\xFF\xFF\xFF\n"); const ch: Uint8Array[] = [h]; const of: number[] = [0]; let off = h.length;
    for (let i = 0; i < this.o.length; i++) {
      const r = this.o[i]; if (!r) throw new Error(`Missing obj ${i + 1}`);
      const p = enc(`${i + 1} 0 obj\n`), b = typeof r === "string" ? enc(r) : r, s = enc("\nendobj\n");
      of.push(off); ch.push(p, b, s); off += p.length + b.length + s.length;
    }
    const xs = off; let xr = `xref\n0 ${this.o.length + 1}\n0000000000 65535 f \n`;
    for (let i = 1; i < of.length; i++) xr += `${String(of[i]).padStart(10, "0")} 00000 n \n`;
    ch.push(enc(xr), enc(`trailer\n<< /Size ${this.o.length + 1} /Root ${root} 0 R >>\nstartxref\n${xs}\n%%EOF`));
    return cat(ch);
  }
}
function strObj(d: string, data: Uint8Array) {
  return cat([enc(`<< ${d} /Length ${data.length} >>\nstream\n`), data, enc("\nendstream")]);
}

// ── Canvas helpers ──────────────────────────────────────────────────────────

async function loadLogo(src?: string): Promise<HTMLImageElement | null> {
  if (!src || typeof Image === "undefined") return null;
  return new Promise(r => { const i = new Image(); i.onload = () => r(i); i.onerror = () => r(null); i.src = src; });
}

function T(ctx: CanvasRenderingContext2D, x: number, y: number, s: string, font: string, color: string, align: CanvasTextAlign = "left") {
  ctx.font = font; ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = "top";
  ctx.fillText(safe(s), x, y);
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath(); ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr); ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr); ctx.arcTo(x, y, x + w, y, rr); ctx.closePath();
}

function cardRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, bg: string, border: string, radius = u(4)) {
  rr(ctx, x, y, w, h, radius);
  ctx.fillStyle = bg; ctx.fill();
  ctx.strokeStyle = border; ctx.lineWidth = u(0.5); ctx.stroke();
}

function hline(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, color: string) {
  ctx.fillStyle = color; ctx.fillRect(x, y, w, u(0.33));
}

// ── Text wrapping ───────────────────────────────────────────────────────────

function splitLong(ctx: CanvasRenderingContext2D, tok: string, maxW: number): string[] {
  const p: string[] = []; let c = "";
  for (const ch of tok) { const n = c + ch; if (c && ctx.measureText(n).width > maxW) { p.push(c); c = ch; } else c = n; }
  if (c) p.push(c); return p.length ? p : [tok];
}

function wrap(ctx: CanvasRenderingContext2D, v: string, maxW: number, font: string, maxLines = 999): string[] {
  const t = safe(v); if (!t) return [""]; ctx.font = font;
  const words = t.split(" ").filter(Boolean);
  const exp: string[] = [];
  for (const w of words) { if (ctx.measureText(w).width <= maxW) exp.push(w); else exp.push(...splitLong(ctx, w, maxW)); }
  const lines: string[] = []; let c = "";
  for (const tok of exp) { const cand = c ? c + " " + tok : tok; if (!c || ctx.measureText(cand).width <= maxW) c = cand; else { lines.push(c); c = tok; } }
  if (c) lines.push(c);
  return lines.slice(0, maxLines);
}

// ── Table column positions ──────────────────────────────────────────────────

type Cols = { tX: number; tW: number; numX: number; cpX: number; cpW: number; flX: number; stX: number; tmX: number };
function getCols(): Cols {
  const tX = MARGIN, tW = CW;
  const numW = u(22), flW = u(58), stW = u(56), tmW = u(96);
  const cpW = tW - numW - flW - stW - tmW;
  const cpEnd = tX + numW + cpW;
  return { tX, tW, numX: tX + u(8), cpX: tX + numW, cpW: cpW - u(8), flX: cpEnd, stX: cpEnd + flW, tmX: cpEnd + flW + stW };
}

// ── Row preparation ─────────────────────────────────────────────────────────

const PILL_H = u(6) + u(3) * 2;
const PILL_GAP = u(3);

function buildBadges(r: TourReportCheckpointRow): RowBadge[] {
  const out: RowBadge[] = [];
  if (r.isDoubleHit) out.push({ label: "DH", bg: C.doubleHitBg, border: C.doubleHitBorder, text: C.doubleHitText });
  if (r.isQuickPatrol) out.push({ label: "QP", bg: C.quickPatrolBg, border: C.quickPatrolBorder, text: C.quickPatrolText });
  if (r.hasRuleViolation) out.push({ label: "RV", bg: C.violationBg, border: C.violationBorder, text: C.violationText });
  return out;
}

function prepRows(ctx: CanvasRenderingContext2D, rows: TourReportCheckpointRow[], c: Cols): PreparedRow[] {
  const pad = u(7);
  return rows.map(r => {
    const lines = wrap(ctx, r.checkpointName || "Checkpoint", c.cpW, F.td, 3);
    const textH = lines.length * F.LH_TD;
    const badges = buildBadges(r);
    const contentH = Math.max(textH, PILL_H);
    return { index: r.index, status: r.status, scanTimeLabel: safe(r.scanTimeLabel || "-"), nameLines: lines, badges, rowHeight: Math.max(u(24), contentH + pad * 2) };
  });
}

// ── Status pill ─────────────────────────────────────────────────────────────

function drawPillCustom(ctx: CanvasRenderingContext2D, x: number, topY: number, label: string, bg: string, border: string, fg: string) {
  ctx.font = F.pill;
  const tw = ctx.measureText(label).width;
  const hPad = u(5), vPad = u(3);
  const pw = tw + hPad * 2, ph = u(6) + vPad * 2;

  rr(ctx, x, topY, pw, ph, u(10));
  ctx.fillStyle = bg; ctx.fill();
  ctx.strokeStyle = border; ctx.lineWidth = u(0.5); ctx.stroke();
  T(ctx, x + hPad, topY + vPad, label, F.pill, fg);
}

function drawPill(ctx: CanvasRenderingContext2D, x: number, topY: number, status: "Scanned" | "Missed") {
  const scanned = status === "Scanned";
  drawPillCustom(
    ctx, x, topY,
    scanned ? "SCANNED" : "MISSED",
    scanned ? C.pillScannedBg : C.pillMissedBg,
    scanned ? C.pillScannedBorder : C.pillMissedBorder,
    scanned ? C.pillScannedText : C.pillMissedText,
  );
}

// ── Footer ──────────────────────────────────────────────────────────────────

function drawFooter(ctx: CanvasRenderingContext2D, page: number, total: number) {
  hline(ctx, MARGIN, FOOTER_AT, CW, C.divider);
  const fy = FOOTER_AT + u(5);
  T(ctx, MARGIN, fy, "ALPINE PROTECTIVE SOLUTIONS  /  602 14TH STREET, MODESTO CA 95354", F.footer, C.textMuted);
  T(ctx, PW - MARGIN, fy, `PAGE ${page} / ${total}`, F.footer, C.textMuted, "right");
}

// ── Page 1 header ───────────────────────────────────────────────────────────

function drawPage1Header(ctx: CanvasRenderingContext2D, input: TourReportPdfInput, _logo: HTMLImageElement | null): number {
  // Accent stripe
  ctx.fillStyle = C.accent;
  ctx.fillRect(0, 0, PW, u(4));
  const stripeBottom = u(4);

  // Measure info box dimensions
  const smallFont = mk(800, 6, MONO);
  const valFont = mk(500, 6, MONO);
  const lineH = u(8);
  const boxPad = u(7);
  const rowGap = u(5);
  const boxH = boxPad + lineH + rowGap + lineH + boxPad;

  ctx.font = smallFont;
  const gap = u(3);
  const colSep = u(12);
  const leftLblW = Math.max(ctx.measureText("TOUR").width, ctx.measureText("GUARD").width) + gap;
  const rightLblW = Math.max(ctx.measureText("DATE").width, ctx.measureText("TIME").width) + gap;

  ctx.font = valFont;
  const leftColW = leftLblW + Math.max(ctx.measureText(safe(input.tourName) || "-").width, ctx.measureText(safe(input.guardName) || "-").width);
  const rightColW = rightLblW + Math.max(ctx.measureText(safe(input.reportDateLabel) || "-").width, ctx.measureText(safe(input.reportTimeLabel) || "-").width);
  const boxW = boxPad + leftColW + colSep + rightColW + boxPad;

  // Title block height
  const titleBlockH = F.LH_TITLE + u(10) + u(3);
  const zonePad = u(5);
  const zoneH = Math.max(titleBlockH, boxH) + zonePad * 2;
  const ruleY = stripeBottom + zoneH;

  // Title left — bottom-aligned to sit close to the accent rule
  const titleY = stripeBottom + zoneH - zonePad - titleBlockH;
  T(ctx, MARGIN, titleY, "TOUR PERFORMANCE REPORT", F.title, C.text);
  T(ctx, MARGIN, titleY + F.LH_TITLE, `SESSION #${input.tourSessionId}`, F.titleSub, C.textMuted);

  // Info box — right-aligned, vertically centered in zone
  const boxY = stripeBottom + Math.floor((zoneH - boxH) / 2);
  const boxX = PW - MARGIN - boxW;
  cardRect(ctx, boxX, boxY, boxW, boxH, C.cardBg, C.cardBorder);

  const lx = boxX + boxPad;
  const rx = lx + leftColW + colSep;

  const r1mid = boxY + boxPad + Math.floor(lineH / 2);
  Tmid(ctx, lx, r1mid, "TOUR", smallFont, C.textMuted);
  Tmid(ctx, lx + leftLblW, r1mid, safe(input.tourName) || "-", valFont, C.text);
  Tmid(ctx, rx, r1mid, "DATE", smallFont, C.textMuted);
  Tmid(ctx, rx + rightLblW, r1mid, safe(input.reportDateLabel) || "-", valFont, C.text);

  const r2mid = boxY + boxPad + lineH + rowGap + Math.floor(lineH / 2);
  Tmid(ctx, lx, r2mid, "GUARD", smallFont, C.textMuted);
  Tmid(ctx, lx + leftLblW, r2mid, safe(input.guardName) || "-", valFont, C.text);
  Tmid(ctx, rx, r2mid, "TIME", smallFont, C.textMuted);
  Tmid(ctx, rx + rightLblW, r2mid, safe(input.reportTimeLabel) || "-", valFont, C.text);

  // Accent rule
  ctx.fillStyle = C.accent;
  ctx.fillRect(MARGIN, ruleY, CW, u(1.5));
  let y = ruleY + u(1.5) + u(10);

  return y;
}

// ── Metric cards ────────────────────────────────────────────────────────────

function drawMetrics(ctx: CanvasRenderingContext2D, input: TourReportPdfInput, startY: number): number {
  let y = startY;

  T(ctx, MARGIN, y, "SUMMARY", F.sectionLbl, C.accent);
  y += F.LH_SECTION + u(6);

  const gap = u(6);
  const cardW = Math.floor((CW - gap * 2) / 3);
  const pad = u(10);
  const valueMaxW = cardW - pad - u(8);

  const metrics: [string, string][] = [
    [safe(input.averageSitePatrolTimeLabel) || "-", "AVG SITE PATROL"],
    [safe(input.averageTravelTimeLabel)     || "-", "AVG TRAVEL"],
    [safe(input.realTourDurationLabel)      || "-", "TOUR DURATION"],
    [safe(input.siteDoubleHitsLabel)        || "-", "DOUBLE HITS"],
    [safe(input.siteQuickPatrolsLabel)      || "-", "QUICK PATROLS"],
    [safe(input.checkpointCompletionLabel)  || "-", "COMPLETION"],
  ];

  // Pre-measure all values to find the tallest card needed
  const allWrapped = metrics.map(([val]) => wrap(ctx, val, valueMaxW, F.metricVal));
  let maxValLines = 1;
  for (const lines of allWrapped) { if (lines.length > maxValLines) maxValLines = lines.length; }

  const labelTopPad = u(8);
  const labelToValue = u(4);
  const bottomPad = u(8);
  const cardH = labelTopPad + F.LH_MLBL + labelToValue + (maxValLines * F.LH_MVAL) + bottomPad;

  for (let i = 0; i < metrics.length; i++) {
    const row = Math.floor(i / 3), col = i % 3;
    const cx = MARGIN + col * (cardW + gap);
    const cy = y + row * (cardH + gap);
    const [, lbl] = metrics[i];
    const valLines = allWrapped[i];

    cardRect(ctx, cx, cy, cardW, cardH, C.cardBg, C.cardBorder);

    // Accent left edge
    ctx.save();
    rr(ctx, cx, cy, cardW, cardH, u(4));
    ctx.clip();
    ctx.fillStyle = C.accent;
    ctx.fillRect(cx, cy, u(3), cardH);
    ctx.restore();

    // Label
    T(ctx, cx + pad, cy + labelTopPad, lbl, F.metricLbl, C.textMuted);

    // Value lines (start below label)
    let vy = cy + labelTopPad + F.LH_MLBL + labelToValue;
    for (const ln of valLines) { T(ctx, cx + pad, vy, ln, F.metricVal, C.text); vy += F.LH_MVAL; }
  }

  y += 2 * cardH + gap + u(12);
  return y;
}

// ── Continuation header ─────────────────────────────────────────────────────

function drawContHeader(ctx: CanvasRenderingContext2D, input: TourReportPdfInput): number {
  ctx.fillStyle = C.accent;
  ctx.fillRect(0, 0, PW, u(4));
  let y = u(4) + u(12);

  T(ctx, MARGIN, y, "TOUR PERFORMANCE REPORT", F.title, C.text);
  T(ctx, PW - MARGIN, y, safe(`${input.guardName || "-"}  ·  ${input.reportDateLabel || "-"}`), F.titleSub, C.textMuted, "right");
  y += F.LH_TITLE + u(3);

  ctx.fillStyle = C.accent;
  ctx.fillRect(MARGIN, y, CW, u(1.5));
  y += u(1.5) + u(10);

  return y;
}

// ── Checkpoint intro ────────────────────────────────────────────────────────

function drawCheckpointIntro(
  ctx: CanvasRenderingContext2D, y: number,
  total: number, scanned: number, missed: number, completionLabel: string,
): number {
  T(ctx, MARGIN, y, "CHECKPOINTS", F.sectionLbl, C.accent);

  ctx.font = F.summaryTxt;
  ctx.textBaseline = "top";
  const summaryX = MARGIN + u(58);
  const parts = [
    { n: String(scanned), l: "scanned", c: C.pillScannedText },
    { n: String(missed), l: "missed", c: C.pillMissedText },
    { n: String(total), l: "total", c: C.textSec },
    { n: safe(completionLabel) || "-", l: "completion", c: C.textSec },
  ];
  let sx = summaryX;
  const spacer = u(2);
  const itemGap = u(6);

  for (let pi = 0; pi < parts.length; pi++) {
    const p = parts[pi];
    ctx.font = F.summaryTxt;

    ctx.fillStyle = p.c;
    ctx.textAlign = "left";
    ctx.fillText(safe(p.n), sx, y);
    const nw = ctx.measureText(safe(p.n)).width;
    sx += nw + spacer;

    ctx.fillStyle = C.textMuted;
    ctx.fillText(p.l, sx, y);
    const lw = ctx.measureText(p.l).width;
    sx += lw + itemGap;
  }

  y += F.LH_SUMMARY + u(6);
  return y;
}

// ── Table header ────────────────────────────────────────────────────────────

const TH_H = u(22);

function drawTableHeader(ctx: CanvasRenderingContext2D, c: Cols, y: number): number {
  // Round top corners of header bg
  ctx.save();
  rr(ctx, c.tX, y, c.tW, TH_H, u(4));
  ctx.clip();
  ctx.fillStyle = C.headerRowBg;
  ctx.fillRect(c.tX, y, c.tW, TH_H);
  ctx.restore();

  // Header border (top only, rounded)
  rr(ctx, c.tX, y, c.tW, TH_H, u(4));
  ctx.strokeStyle = C.cardBorder; ctx.lineWidth = u(0.5); ctx.stroke();

  const ty = y + u(6);
  T(ctx, c.numX, ty, "#", F.th, C.textMuted);
  T(ctx, c.cpX, ty, "CHECKPOINT", F.th, C.textMuted);
  T(ctx, c.flX, ty, "FLAGS", F.th, C.textMuted);
  T(ctx, c.stX, ty, "STATUS", F.th, C.textMuted);
  T(ctx, c.tmX, ty, "TIME", F.th, C.textMuted);

  y += TH_H;
  return y;
}

// ── Table row ───────────────────────────────────────────────────────────────

function Tmid(ctx: CanvasRenderingContext2D, x: number, midY: number, s: string, font: string, color: string, align: CanvasTextAlign = "left") {
  ctx.font = font; ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = "middle";
  ctx.fillText(safe(s), x, midY);
}

function drawRow(ctx: CanvasRenderingContext2D, c: Cols, row: PreparedRow, y: number, alt: boolean, isLast: boolean): number {
  if (alt) {
    ctx.fillStyle = "rgba(255,255,255,0.015)";
    ctx.fillRect(c.tX, y, c.tW, row.rowHeight);
  }

  const rh = row.rowHeight;
  const mid = y + Math.floor(rh / 2);

  // Index — vertically centered via middle baseline
  Tmid(ctx, c.numX, mid, String(row.index), F.tdMono, C.textMuted);

  // Checkpoint name — multi-line block centered as a unit
  const nameBlockH = row.nameLines.length * F.LH_TD;
  let ny = mid - Math.floor(nameBlockH / 2) + Math.floor(F.LH_TD / 2);
  for (const ln of row.nameLines) { Tmid(ctx, c.cpX, ny, ln, F.td, C.text); ny += F.LH_TD; }

  // Status pill — vertically centered
  drawPill(ctx, c.stX, mid - Math.floor(PILL_H / 2), row.status);

  // Flag badges — horizontally laid out, vertically centered
  let fx = c.flX;
  const pillTop = mid - Math.floor(PILL_H / 2);
  for (const b of row.badges) {
    drawPillCustom(ctx, fx, pillTop, b.label, b.bg, b.border, b.text);
    ctx.font = F.pill;
    const pw = ctx.measureText(b.label).width + u(5) * 2;
    fx += pw + PILL_GAP;
  }

  // Time — vertically centered via middle baseline
  Tmid(ctx, c.tmX, mid, row.scanTimeLabel, F.tdMono, C.textSec);

  const bottom = y + rh;
  if (!isLast) {
    hline(ctx, c.tX + u(6), bottom, c.tW - u(12), C.divider);
  }
  return bottom;
}

// ── Build pages ─────────────────────────────────────────────────────────────

function buildPages(input: TourReportPdfInput, logo: HTMLImageElement | null): HTMLCanvasElement[] {
  const checkpoints = Array.isArray(input.checkpoints) ? input.checkpoints : [];
  const pages: HTMLCanvasElement[] = [];

  const mc = document.createElement("canvas"); mc.width = PW; mc.height = PH;
  const mctx = mc.getContext("2d");
  if (!mctx) return pages;

  const c = getCols();
  const rows = prepRows(mctx, checkpoints, c);
  const total = checkpoints.length;
  const scanned = checkpoints.filter(r => r.status === "Scanned").length;
  const missed = total - scanned;

  let cursor = 0;
  let pageIdx = 0;

  while (cursor < Math.max(1, rows.length)) {
    const canvas = document.createElement("canvas"); canvas.width = PW; canvas.height = PH;
    const ctx = canvas.getContext("2d");
    if (!ctx) break;

    // Dark page background
    ctx.fillStyle = C.pageBg;
    ctx.fillRect(0, 0, PW, PH);

    let y: number;
    if (pageIdx === 0) {
      y = drawPage1Header(ctx, input, logo);
      y = drawMetrics(ctx, input, y);
    } else {
      y = drawContHeader(ctx, input);
    }

    y = drawCheckpointIntro(ctx, y, total, scanned, missed, input.checkpointCompletionLabel || "-");

    // Table
    const tableTop = y;
    y = drawTableHeader(ctx, c, y);

    let zebra = 0;
    if (rows.length === 0) {
      T(ctx, c.cpX, y + u(10), "No checkpoint scans were recorded for this tour.", F.td, C.textMuted);
      y += u(28);
      cursor = 1;
    } else {
      while (cursor < rows.length) {
        const row = rows[cursor];
        if (y + row.rowHeight > CONTENT_BOTTOM) break;
        const isLast = cursor === rows.length - 1 || y + row.rowHeight + rows[Math.min(cursor + 1, rows.length - 1)].rowHeight > CONTENT_BOTTOM;
        y = drawRow(ctx, c, row, y, zebra % 2 === 1, isLast);
        cursor++;
        zebra++;
      }
      if (cursor < rows.length) {
        T(ctx, PW - MARGIN - u(6), y + u(4), "continued on next page...", F.footer, C.textDim, "right");
      }
    }

    // Table bottom border (rounded bottom corners)
    const tableH = y - tableTop;
    ctx.save();
    rr(ctx, c.tX, tableTop, c.tW, tableH, u(4));
    ctx.strokeStyle = C.cardBorder; ctx.lineWidth = u(0.5); ctx.stroke();
    ctx.restore();

    pages.push(canvas);
    pageIdx++;
  }

  for (let i = 0; i < pages.length; i++) {
    const ctx = pages[i].getContext("2d");
    if (ctx) drawFooter(ctx, i + 1, pages.length);
  }
  return pages;
}

// ── Assemble PDF ────────────────────────────────────────────────────────────

function toImg(canvas: HTMLCanvasElement): PdfImage {
  const url = canvas.toDataURL("image/jpeg", 0.97);
  const i = url.indexOf(",");
  return { width: canvas.width, height: canvas.height, bytes: i >= 0 ? b64(url.slice(i + 1)) : new Uint8Array() };
}

function makePdf(imgs: PdfImage[]) {
  const b = new PdfBuilder(); const pid = b.res(); const pids: number[] = [];
  for (const img of imgs) {
    const iid = b.add(strObj(`/Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode`, img.bytes));
    const cid = b.add(strObj("", enc(`q ${PDF_W} 0 0 ${PDF_H} 0 0 cm /Im0 Do Q`)));
    pids.push(b.add(`<< /Type /Page /Parent ${pid} 0 R /MediaBox [0 0 ${PDF_W} ${PDF_H}] /Resources << /XObject << /Im0 ${iid} 0 R >> >> /Contents ${cid} 0 R >>`));
  }
  b.put(pid, `<< /Type /Pages /Kids [${pids.map(id => `${id} 0 R`).join(" ")}] /Count ${pids.length} >>`);
  return b.build(b.add(`<< /Type /Catalog /Pages ${pid} 0 R >>`));
}

export async function generateTourReportPdfBlob(input: TourReportPdfInput): Promise<Blob> {
  const logo = await loadLogo(input.logoSrc);
  const bytes = makePdf(buildPages(input, logo).map(toImg));
  const copy = new Uint8Array(bytes.length); copy.set(bytes);
  return new Blob([copy.buffer], { type: "application/pdf" });
}
